import { useEffect } from 'react';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useFinalcutContext } from 'components/app/finalcuts/_mediator/finalcut.provider';
import { setFcPlayback } from 'components/app/finalcuts/_mediator/fc-controls.slice';
import { useFinalcutCb } from 'components/app/finalcuts/_mediator/use-finalcut-cb';

import { Player } from 'components/UI/player/player';
import { OnProgress } from 'components/app/app-components/player/player';
import styles from './_finalcuts-player.module.scss';

const MAX_BUFFER: Seconds = 3;

export const FinalcutsPlayer = () => {
  const url = useAppSelector((state) => state.fc__data.url);
  const fcRangeTime = useAppSelector((state) => state.fc__data.fcRangeTime);
  const fcPlayback = useAppSelector((state) => state.fc__controls.fcPlayback);
  const { fcPlayerRef, fcPlayerReady, fcPlayerGetDuration } = useFinalcutContext();
  const { fcPause, fcPlay, fcSeekTo } = useFinalcutCb();
  const { wsGetCursorTime } = useAudioTrackContext();
  const dispatch = useAppDispatch();

  const onInit = (init: boolean) => {
    if (!init) return;
    fcSeekTo(wsGetCursorTime('fraction') ?? fcRangeTime?.startTime ?? 0);
  };

  const onBuffer = () => {
    fcPlayerReady.current = false;
    fcPause();
  };

  const onEnded = () => {
    dispatch(setFcPlayback(false));
    fcPause();
  };

  const onReady = () => {
    fcPlayerReady.current = true;
    if (fcPlayback) fcPlay();
  };

  const onProgress = ({ playedSeconds, loadedSeconds }: OnProgress) => {
    // NOTE: we need 'onProgress' because 'onReady' doesn't always work
    if (fcPlayerReady.current) return;
    const loaded = loadedSeconds - playedSeconds;
    if (loaded > MAX_BUFFER || loaded >= fcPlayerGetDuration()) fcSeekTo(wsGetCursorTime('fraction'));
  };

  useEffect(() => {
    return () => {
      fcPlayerReady.current = false;
    };
  }, [url]);

  return (
    <div className={styles.root}>
      <Player
        ref={fcPlayerRef}
        config={{ attributes: { crossOrigin: 'anonymous' } }}
        controls={true}
        muted={false}
        onBuffer={onBuffer}
        onProgress={onProgress}
        onInitPlayer={onInit}
        onReady={onReady}
        onEnded={onEnded}
        url={url}
      />
    </div>
  );
};
